import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { isSuccess } from 'src/utils/errorUtils';
import {
  ApiException,
  Feature,
  SaleCancellationReason,
  SaleClient,
} from 'src/WebApiController';

import { SALES_GROUP_QUERY_KEY } from './useGetSalesGroup';

/**
 * Group-level bulk Reject / Cancel(Deallocate) actions for the Sales Group modal
 * (POS-8632). Rather than relying on a single shared group-level write endpoint,
 * these actions fan out CLIENT-SIDE over the group's eligible member sales using
 * the existing per-sale endpoints (SaleClient.rejectSale /
 * SaleClient.deallocateSale), one call per member, in parallel via
 * Promise.allSettled. No new backend endpoint is introduced.
 *
 * Because members are processed independently, some may succeed while others
 * fail. A member counts as a success only when its call fulfilled AND the
 * returned PosUiActionResult isSuccess; every rejected promise or non-OK result
 * is a failure. onSettled always invalidates the getSalesGroup query so the modal
 * reflects reality (partial applies included), and onSuccess surfaces a
 * "{succeeded} of {total} succeeded, {failed} failed" summary via showErrorDialog
 * when any member failed.
 *
 * Gated by Feature.SalesGroupView (matches useGetSalesGroup); the mutations throw
 * when the feature is disabled so callers never fire no-op requests.
 */
export function useSalesGroupBulkActions(salesGroupId: string) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError, showErrorDialog } = useClientLogContext();
  const queryClient = useQueryClient();
  const hasSalesGroupViewFeature = useUserHasFeature(Feature.SalesGroupView);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: [SALES_GROUP_QUERY_KEY, salesGroupId],
    });

  const summarizeAndReport = (
    errorId: 'SaleClient.rejectSale' | 'SaleClient.deallocateSale',
    results: PromiseSettledResult<
      Awaited<ReturnType<SaleClient['rejectSale']>>
    >[],
    saleIds: number[]
  ) => {
    const total = results.length;
    const succeeded = results.filter(
      (r) => r.status === 'fulfilled' && isSuccess(r.value)
    ).length;
    const failed = total - succeeded;

    if (failed > 0) {
      showErrorDialog(
        errorId,
        {
          message: `${succeeded} of ${total} succeeded, ${failed} failed`,
          status: 0,
        } as ApiException,
        {
          trackErrorData: { salesGroupId, saleIds },
        }
      );
    }
  };

  const rejectSales = useMutation({
    mutationFn: async ({
      saleIds,
      cancellationReason,
    }: {
      saleIds: number[];
      cancellationReason: SaleCancellationReason | null;
    }) => {
      if (!hasSalesGroupViewFeature) {
        throw new Error('Sales Group feature is not enabled');
      }
      const client = new SaleClient(activeAccountWebClientConfig);
      return await Promise.allSettled(
        // POS-8632: mirror single-sale reject (useCancelRetailSale) — defer keep-tickets to backend default
        saleIds.map((id) => client.rejectSale(id, null, cancellationReason))
      );
    },
    onError: (e) =>
      trackError('SaleClient.rejectSale', e as ErrorTypes, { salesGroupId }),
    onSuccess: (results, variables) =>
      summarizeAndReport('SaleClient.rejectSale', results, variables.saleIds),
    onSettled: invalidate,
  });

  const deallocateSales = useMutation({
    mutationFn: async ({
      saleIds,
      adminHold,
    }: {
      saleIds: number[];
      adminHold: boolean;
    }) => {
      if (!hasSalesGroupViewFeature) {
        throw new Error('Sales Group feature is not enabled');
      }
      const client = new SaleClient(activeAccountWebClientConfig);
      return await Promise.allSettled(
        saleIds.map((id) => client.deallocateSale(id, adminHold))
      );
    },
    onError: (e) =>
      trackError('SaleClient.deallocateSale', e as ErrorTypes, {
        salesGroupId,
      }),
    onSuccess: (results, variables) =>
      summarizeAndReport(
        'SaleClient.deallocateSale',
        results,
        variables.saleIds
      ),
    onSettled: invalidate,
  });

  return { rejectSales, deallocateSales };
}
